import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Building2,
  Check,
  ChevronDown,
  CheckCircle2,
  Copy,
  CreditCard,
  FileImage,
  FileText,
  Home,
  Landmark,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Power,
  RefreshCw,
  ShieldCheck,
  Ban,
  UserCheck,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { env } from "../../config/env";
import { restaurantAdminService, rowsOf, unwrap } from "../../services/restaurantAdmin.service";
import {
  Button,
  Card,
  EmptyState,
  ErrorBox,
  Input,
  LoadingBox,
  Select,
  Textarea,
} from "./components/FoodAdminUi";

const PERMISSION_OPTIONS = [
  { key: "restaurant.panel.access", label: "Panel access" },
  { key: "restaurant.orders.read", label: "Read orders" },
  { key: "restaurant.products.read", label: "Read products" },
  { key: "restaurant.products.manage", label: "Manage product requests" },
  { key: "restaurant.reports.read", label: "Read reports" },
  { key: "restaurant.employees.read", label: "Read employees" },
  { key: "restaurant.employees.manage", label: "Manage employees" },
];

const DEFAULT_PERMISSIONS = PERMISSION_OPTIONS.map((p) => p.key);
const PAGE_SIZE = 8;
const ROW_SIZE_OPTIONS = [8, 10, 25, 50];

const blankForm = {
  restaurantId: "",
  name: "",
  email: "",
  phone: "",
  password: "",
  role: "restaurant_manager",
  permissions: DEFAULT_PERMISSIONS,
  isActive: true,
  status: "ACTIVE",
  mustChangePassword: true,
};

function restaurantIdOf(row) {
  return row?.id || row?.restaurantId || row?.restaurant_id || "";
}

function employeeIdOf(row) {
  return row?.id || row?.employeeId || row?.employee_id || "";
}

function getRestaurantName(restaurants, id) {
  const found = restaurants.find((r) => String(restaurantIdOf(r)) === String(id));
  return found?.name || id || "-";
}

function normalisePerms(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function approvalStatusOf(row) {
  return String(row?.approvalStatus || row?.approval_status || row?.onboardingApprovalStatus || row?.onboarding_approval_status || "NOT_SUBMITTED").toUpperCase();
}

function onboardingCompletedOf(row) {
  return Boolean(row?.onboardingCompleted ?? row?.onboarding_completed);
}

function onboardingPayloadOf(row) {
  return row?.onboardingPayload || row?.onboarding_payload || row?.onboarding || {};
}

function toObject(value, fallback = {}) {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch (_) {
    return fallback;
  }
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === "") return [];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {}
    return value.split(",").map((x) => x.trim()).filter(Boolean);
  }
  return [value];
}

function textOf(value, fallback = "-") {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return fallback;
}

function firstOf(obj, keys, fallback = undefined) {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null && obj[key] !== "") return obj[key];
  }
  return fallback;
}

function onboardingSections(value) {
  const root = toObject(value, {});
  const onboarding = toObject(root.onboarding || root.profile || root.data || {}, {});
  const merged = { ...root, ...onboarding };
  const addressSource = firstOf(merged, ["address", "employeeAddress", "permanentAddress", "currentAddress", "completeAddress"], {});
  const rawAddress = toObject(addressSource, {});
  const address = {
    ...rawAddress,
    addressLine1: firstOf(rawAddress, ["addressLine1", "address_line1", "address_line_1", "line1"], firstOf(merged, ["addressLine1", "address_line1", "address_line_1", "line1"])),
    addressLine2: firstOf(rawAddress, ["addressLine2", "address_line2", "address_line_2", "line2"], firstOf(merged, ["addressLine2", "address_line2", "address_line_2", "line2"])),
    city: firstOf(rawAddress, ["city"], firstOf(merged, ["city"])),
    state: firstOf(rawAddress, ["state"], firstOf(merged, ["state"])),
    pincode: firstOf(rawAddress, ["pincode", "pinCode", "postalCode", "zip"], firstOf(merged, ["pincode", "pinCode", "postalCode", "zip"])),
  };
  const bankSource = firstOf(merged, ["bank", "bankDetails", "bank_details", "bankAccount"], {});
  const rawBank = toObject(bankSource, {});
  const bank = {
    ...rawBank,
    accountHolderName: firstOf(rawBank, ["accountHolderName", "account_holder_name", "holderName"], firstOf(merged, ["accountHolderName", "account_holder_name", "holderName", "bankHolderName"])),
    bankName: firstOf(rawBank, ["bankName", "bank_name"], firstOf(merged, ["bankName", "bank_name"])),
    accountNumber: firstOf(rawBank, ["accountNumber", "account_number"], firstOf(merged, ["accountNumber", "account_number", "bankAccount"])),
    ifscCode: firstOf(rawBank, ["ifscCode", "ifsc", "ifsc_code", "bankIfsc", "bank_ifsc"], firstOf(merged, ["ifscCode", "ifsc", "ifsc_code", "bankIfsc", "bank_ifsc"])),
    accountType: firstOf(rawBank, ["accountType", "account_type"], firstOf(merged, ["accountType", "account_type"])),
    upiId: firstOf(rawBank, ["upiId", "upi_id", "upi"], firstOf(merged, ["upiId", "upi_id", "upi"])),
  };
  const documents = toObject(firstOf(merged, ["documents", "docs", "files"], {}), {});
  return { root, merged, address, bank, documents };
}

function normalizeLabel(value) {
  return String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function roleLabel(value) {
  return normalizeLabel(String(value || "restaurant_staff").replace(/^restaurant_/, ""));
}

function fileBaseUrl() {
  const raw = env.AUTH_BASE_URL || env.RESTAURANT_APP_BASE_URL || env.USER_BASE_URL || window.location.origin;
  return String(raw || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
}

function absoluteFileUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  const base = fileBaseUrl();
  if (!base) return raw;
  return raw.startsWith("/") ? `${base}${raw}` : `${base}/${raw}`;
}

function documentObject(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  return { url: value };
}

function documentUrl(value) {
  const obj = documentObject(value);
  return absoluteFileUrl(obj.url || obj.path || obj.fileUrl || obj.file_url || obj.preview || obj.href || obj.location || obj.filename || "");
}

function isImageDocument(value) {
  const obj = documentObject(value);
  const url = documentUrl(value);
  const mime = String(obj.mimeType || obj.mimetype || obj.type || "");
  return mime.startsWith("image/") || /\.(png|jpg|jpeg|webp|gif|bmp|svg)(\?|#|$)/i.test(url);
}

function pickDocument(documents, merged, keys) {
  return firstOf(documents, keys, firstOf(merged, keys));
}

const EMPLOYEE_DOCUMENTS = [
  {
    key: "aadhaar",
    label: "Aadhaar image",
    keys: ["aadhaar", "aadhar", "aadhaarImage", "aadharImage", "aadhaar_image", "aadhar_image", "aadhaarCard", "aadharCard", "aadhaarCardImage", "aadharCardImage", "aadhaar_document", "aadhar_document", "adharImage", "adhaarImage"],
  },
  {
    key: "pan",
    label: "PAN image",
    keys: ["pan", "panImage", "pan_image", "panCard", "pan_card", "panCardImage", "pan_card_image", "pan_document"],
  },
  {
    key: "bankPassbook",
    label: "Bank passbook image",
    keys: ["bankPassbook", "bank_passbook", "bankPassbookImage", "bank_passbook_image", "passbook", "passbookImage", "passbook_image", "bankProof", "bank_proof", "bankProofImage", "bank_proof_image", "bankDocument", "bank_document", "cancelledCheque", "cancelled_cheque"],
  },
];

const KNOWN_DOCUMENT_KEYS = new Set(EMPLOYEE_DOCUMENTS.flatMap((item) => item.keys));

function DetailTile({ icon, label, value }) {
  return (
    <div className="rounded-[16px] border border-[#f2c6cc] bg-[#fffafb] p-4">
      <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {icon ? <span className="text-[#d86a78]">{icon}</span> : null}
        {label}
      </div>
      <div className="break-words text-[14px] font-semibold text-slate-900">{textOf(value)}</div>
    </div>
  );
}

function ProfileSection({ icon, title, children }) {
  return (
    <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-4">
      <h3 className="mb-3 flex items-center gap-2 text-[15px] font-semibold text-[#d86a78]">
        {icon ? <span className="text-[#d86a78]">{icon}</span> : null}
        {title}
      </h3>
      {children}
    </div>
  );
}

function DocumentCard({ label, value }) {
  const obj = documentObject(value);
  const url = documentUrl(value);
  const isImage = isImageDocument(value);
  const fileText = obj.originalName || obj.name || obj.fileName || obj.filename || (url ? "Uploaded file" : "Not uploaded");

  return (
    <div className="rounded-[16px] border border-[#f2c6cc] bg-white p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[12px] font-semibold uppercase text-slate-500">
          <FileText size={15} className="text-[#d86a78]" />
          {label}
        </div>
        {url ? <a href={url} target="_blank" rel="noreferrer" className="text-[11px] font-semibold text-[#d86a78] hover:underline">Open</a> : null}
      </div>
      {url && isImage ? (
        <a href={url} target="_blank" rel="noreferrer" className="block">
          <img
            src={url}
            alt={label}
            className="h-36 w-full rounded-[12px] object-cover"
            onError={(event) => {
              event.currentTarget.style.display = "none";
              const fallback = event.currentTarget.parentElement?.nextElementSibling;
              if (fallback) fallback.classList.remove("hidden");
            }}
          />
        </a>
      ) : null}
      <div className={`${url && isImage ? "hidden" : "flex"} h-28 items-center justify-center rounded-[12px] bg-[#fff6f7] text-slate-400`}>
        <FileImage size={24} />
      </div>
      <div className="mt-2 truncate text-[11px] font-semibold text-slate-400">{fileText}</div>
    </div>
  );
}

function EmployeeDocumentCards({ documents = {}, merged = {} }) {
  const cards = EMPLOYEE_DOCUMENTS.map((item) => ({
    ...item,
    value: pickDocument(documents, merged, item.keys),
  }));

  const extraEntries = Object.entries(toObject(documents, {})).filter(([key, value]) => {
    return !KNOWN_DOCUMENT_KEYS.has(key) && value !== undefined && value !== null && value !== "";
  });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {cards.map((item) => <DocumentCard key={item.key} label={item.label} value={item.value} />)}
      </div>
      {extraEntries.length ? (
        <div>
          <div className="mb-2 text-[12px] font-bold uppercase tracking-wide text-slate-400">Other uploaded files</div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {extraEntries.map(([key, value]) => <DocumentCard key={key} label={normalizeLabel(key)} value={value} />)}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function IconAction({ title, tone = "neutral", onClick, disabled = false, children }) {
  const toneClasses = {
    neutral: "text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900",
    review: "text-[#5f6f89] hover:border-[#b8c3d6] hover:bg-[#f5f8ff] hover:text-[#32445f]",
    success: "text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700",
    danger: "text-rose-500 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600",
  };

  return (
    <button 
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-transparent bg-transparent transition disabled:cursor-not-allowed disabled:opacity-50 ${toneClasses[tone] || toneClasses.neutral}`}
    >
      {children}
    </button>
  );
}


function ThemedDropdown({ label, value, onChange, options = [], disabled = false }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selected = options.find((item) => String(item.value) === String(value)) || options[0] || { label: "Select", value: "" };

  useEffect(() => {
    function handleOutsideClick(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <div ref={wrapperRef} className="relative min-w-0">
      {label ? <label className="mb-1.5 block text-[12px] font-bold text-slate-600">{label}</label> : null}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-11 w-full items-center justify-between gap-3 rounded-[12px] border border-[#f2c6cc] bg-white px-4 text-left text-[13px]  text-slate-800 shadow-sm outline-none transition hover:border-[#d86a78] focus:border-[#d86a78] focus:ring-4 focus:ring-[#d86a78]/10 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
      >
        <span className="truncate">{selected.label}</span>
        <ChevronDown size={16} className={`shrink-0 text-[#d86a78] transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-auto rounded-[16px] border border-[#f2c6cc] bg-white p-1.5 ">
          {options.map((item) => {
            const active = String(item.value) === String(value);
            return (
              <button
                key={`${item.value}-${item.label}`}
                type="button"
                onClick={() => {
                  onChange(item.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-3 rounded-[12px] px-3 py-2.5 text-left text-[13px]  transition ${
                  active
                    ? "bg-[#fff0f2] text-[#d86a78]"
                    : "text-slate-700 hover:bg-[#fff8f9] hover:text-[#d86a78]"
                }`}
              >
                <span className="truncate">{item.label}</span>
                {active ? <Check size={15} className="shrink-0" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function StatusPill({ value }) {
  const label = String(value || "-").toUpperCase();
  const positive = new Set(["ACTIVE", "APPROVED", "COMPLETE", "COMPLETED", "CHANGED", "SUCCESS", "VERIFIED"]);
  const warning = new Set(["PENDING", "WAITING", "IN_REVIEW", "REVIEW"]);
  const danger = new Set(["CHANGE_REQUIRED", "NOT_SUBMITTED", "REJECTED", "BLOCKED", "FAILED", "INACTIVE"]);

  let className = "bg-slate-100 text-slate-600";
  if (positive.has(label)) className = "bg-emerald-50 text-emerald-700";
  else if (warning.has(label)) className = "bg-amber-50 text-amber-700";
  else if (danger.has(label)) className = "bg-rose-50 text-rose-600";

  return (
    <span className={`inline-flex min-h-[26px] items-center justify-center rounded-full px-3 py-1 text-[10px] font-bold uppercase leading-none ${className}`}>
      {label}
    </span>
  );
}


function SummaryMetricCard({ label, value, tone = "pink" }) {
  const toneClasses = {
    pink: "border-[#f2c6cc] bg-white text-[#d86a78]",
    yellow: "border-[#f2c6cc] bg-white text-[#d86a78]",
    green: "border-[#f2c6cc] bg-white text-[#d86a78]",
    red: "border-[#f2c6cc] bg-white text-[#d86a78]",
  };

  return (
    <div className={`rounded-[18px] border p-4 ${toneClasses[tone] || toneClasses.pink}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide opacity-90">{label}</div>
      <div className="mt-2 text-[28px] font-bold leading-none">{value}</div>
    </div>
  );
}

function OnboardingDetails({ value, employee }) {
  const base = toObject(employee, {});
  const { merged, address, bank, documents } = onboardingSections(value);
  const fullAddress = firstOf(address, ["fullAddress", "full_address"], [address.addressLine1, address.addressLine2, address.city, address.state, address.pincode].filter(Boolean).join(", "));

  const personal = {
    name: firstOf(merged, ["name", "fullName", "employeeName"], base.name),
    email: firstOf(merged, ["email"], base.email),
    phone: firstOf(merged, ["phone", "mobile", "contactNumber", "contact_number"], base.phone),
    role: roleLabel(firstOf(merged, ["role", "designation", "position"], base.role)),
    fatherName: firstOf(merged, ["fatherName", "father_name", "father", "parentName", "guardianName"]),
    dob: firstOf(merged, ["dob", "dateOfBirth", "date_of_birth", "birthDate"]),
    gender: firstOf(merged, ["gender"]),
  };

  const identity = {
    aadhaarNumber: firstOf(merged, ["aadhaarNumber", "aadharNumber", "aadhaar_number", "aadhar_number", "aadhaarNo", "aadharNo", "adharNumber", "adhaarNumber"]),
    panNumber: firstOf(merged, ["panNumber", "pan_number", "panNo", "pan", "panCardNumber"]),
  };

  return (
    <div className="space-y-4">
      <ProfileSection icon={<UserCheck size={17} />} title="Personal Details">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          <DetailTile label="Name" value={personal.name} />
          <DetailTile icon={<Mail size={14} />} label="Email" value={personal.email} />
          <DetailTile icon={<Phone size={14} />} label="Phone" value={personal.phone} />
          <DetailTile icon={<Building2 size={14} />} label="Restaurant Role" value={personal.role} />
          <DetailTile label="Father Name" value={personal.fatherName} />
          <DetailTile label="Date of Birth" value={personal.dob} />
          <DetailTile label="Gender" value={personal.gender} />
        </div>
      </ProfileSection>

      <ProfileSection icon={<Home size={17} />} title="Complete Address">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <DetailTile icon={<MapPin size={14} />} label="Full Address" value={fullAddress} />
          </div>
          <DetailTile label="Address Line 1" value={address.addressLine1 || address.line1} />
          <DetailTile label="Address Line 2" value={address.addressLine2 || address.line2} />
          <DetailTile label="City" value={address.city} />
          <DetailTile label="State" value={address.state} />
          <DetailTile label="Pincode" value={address.pincode} />
        </div>
      </ProfileSection>

      <ProfileSection icon={<CreditCard size={17} />} title="KYC & Bank Details">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          <DetailTile label="Aadhaar Number" value={identity.aadhaarNumber} />
          <DetailTile label="PAN Number" value={identity.panNumber} />
          <DetailTile label="Account Holder" value={bank.accountHolderName || personal.name} />
          <DetailTile label="Bank Name" value={bank.bankName} />
          <DetailTile label="Account Number" value={bank.accountNumber} />
          <DetailTile label="IFSC Code" value={bank.ifscCode} />
          <DetailTile label="Account Type" value={bank.accountType} />
          <DetailTile label="UPI ID" value={bank.upiId} />
        </div>
      </ProfileSection>

      <ProfileSection icon={<FileText size={17} />} title="Uploaded Images">
        <EmployeeDocumentCards documents={documents} merged={merged} />
      </ProfileSection>
    </div>
  );
}

function PermissionCheckboxes({ value = [], onChange }) {
  const selected = new Set(normalisePerms(value));
  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next));
  };
  return (
    <div className="w-full max-w-full overflow-hidden">
      <div className="mb-2 flex items-center justify-between gap-3">
        <label className="text-[12px] font-bold text-slate-600">Permissions</label>
        <button
          type="button"
          onClick={() => onChange(DEFAULT_PERMISSIONS)}
          className="text-[12px] font-semibold text-[#d86a78] hover:underline"
        >
          Select default
        </button>
      </div>
      <div className="grid grid-cols-1 gap-2 rounded-[16px] border border-[#f2c6cc] bg-white p-3 sm:grid-cols-2">
        {PERMISSION_OPTIONS.map((item) => (
          <label key={item.key} className="flex cursor-pointer items-start gap-2 rounded-[12px] p-2 text-[12px] font-bold text-slate-700 hover:bg-[#fff0f2]">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={selected.has(item.key)}
              onChange={() => toggle(item.key)}
            />
            <span>
              <span className="block">{item.label}</span>
              <span className="block break-all text-[10px] font-semibold text-slate-400">{item.key}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function FoodEmployeesPage() {
  const [restaurants, setRestaurants] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [restaurantId, setRestaurantId] = useState("");
  const [status, setStatus] = useState("");
  const [approvalStatus, setApprovalStatus] = useState("");
  const [form, setForm] = useState(blankForm);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [createdPassword, setCreatedPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [accessSavingId, setAccessSavingId] = useState("");
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const didInitialLoadRef = useRef(false);

  const selectedRestaurantName = useMemo(() => getRestaurantName(restaurants, form.restaurantId), [restaurants, form.restaurantId]);
  const restaurantFilterOptions = useMemo(
    () => [
      { value: "", label: "All restaurants" },
      ...restaurants.map((r) => ({ value: restaurantIdOf(r), label: r.name || "Restaurant" })),
    ],
    [restaurants]
  );

  const statusFilterOptions = useMemo(
    () => [
      { value: "", label: "All" },
      { value: "ACTIVE", label: "ACTIVE" },
      { value: "INACTIVE", label: "INACTIVE" },
      { value: "BLOCKED", label: "BLOCKED" },
    ],
    []
  );

  const approvalFilterOptions = useMemo(
    () => [
      { value: "", label: "All" },
      { value: "NOT_SUBMITTED", label: "NOT_SUBMITTED" },
      { value: "PENDING", label: "PENDING" },
      { value: "APPROVED", label: "APPROVED" },
      { value: "REJECTED", label: "REJECTED" },
    ],
    []
  );

  const employeeSummary = useMemo(() => {
    return employees.reduce(
      (acc, employee) => {
        const approval = approvalStatusOf(employee);
        acc.total += 1;
        if (approval === "PENDING") acc.pending += 1;
        if (approval === "APPROVED") acc.approved += 1;
        if (approval === "REJECTED") acc.rejected += 1;
        return acc;
      },
      { total: 0, pending: 0, approved: 0, rejected: 0 }
    );
  }, [employees]);

  const rowSizeOptions = useMemo(() => {
    const options = [...ROW_SIZE_OPTIONS, pageSize];
    if (employees.length) options.push(employees.length);
    return Array.from(new Set(options)).filter(Boolean).sort((a, b) => a - b);
  }, [employees.length, pageSize]);
  const totalPages = Math.max(1, Math.ceil(employees.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visibleFrom = employees.length ? (safePage - 1) * pageSize + 1 : 0;
  const visibleTo = Math.min(safePage * pageSize, employees.length);
  const paginatedEmployees = useMemo(
    () => employees.slice((safePage - 1) * pageSize, safePage * pageSize),
    [employees, pageSize, safePage]
  );

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function loadRestaurants() {
    const res = await restaurantAdminService.listRestaurants({ limit: 300 });
    return rowsOf(res);
  }

  async function loadEmployees(restRows = restaurants) {
    const params = { q, restaurantId, status, approvalStatus, limit: 300 };
    try {
      const res = await restaurantAdminService.listAllRestaurantEmployees(params);
      const rows = rowsOf(res);
      setEmployees(filterLocal(rows));
      return;
    } catch (err) {
      if (String(err?.message || err || "").includes("restaurant_employees")) throw err;
      const targets = restaurantId ? restRows.filter((r) => String(restaurantIdOf(r)) === String(restaurantId)) : restRows;
      const chunks = await Promise.allSettled(targets.map((r) => restaurantAdminService.listRestaurantEmployees(restaurantIdOf(r))));
      const all = chunks.flatMap((x) => (x.status === "fulfilled" ? rowsOf(x.value) : []));
      setEmployees(filterLocal(all));
    }
  }

  function filterLocal(rows) {
    const query = String(q || "").trim().toLowerCase();
    return rows.filter((e) => {
      const okQ = !query || [e.name, e.email, e.phone].some((v) => String(v || "").toLowerCase().includes(query));
      const okStatus = !status || String(e.status || "").toUpperCase() === String(status).toUpperCase();
      const okApproval = !approvalStatus || approvalStatusOf(e) === String(approvalStatus).toUpperCase();
      return okQ && okStatus && okApproval;
    });
  }

  async function load() {
  setLoading(true);
  setError("");
  try {
    const restRows = await loadRestaurants();
    setRestaurants(restRows);
    await loadEmployees(restRows);
    didInitialLoadRef.current = true;
  } catch (err) {
    setError(err);
  } finally {
    setLoading(false);
  }
}

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [q, restaurantId, status, approvalStatus]);

  useEffect(() => {
  if (!didInitialLoadRef.current) return;

  const timer = window.setTimeout(() => {
    loadEmployees();
  }, 300);

  return () => window.clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [q, restaurantId, status, approvalStatus]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  function startCreate() {
    setEditing(null);
    setCreatedPassword("");
    setForm({ ...blankForm, restaurantId: restaurantId || "" });
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
    setForm({ ...blankForm, restaurantId: restaurantId || "" });
  }

  function startEdit(row) {
    setCreatedPassword("");
    setEditing(row);
    setForm({
      restaurantId: row.restaurantId || row.restaurant_id || "",
      name: row.name || "",
      email: row.email || "",
      phone: row.phone || "",
      password: "",
      role: row.role || "restaurant_manager",
      permissions: normalisePerms(row.permissions || DEFAULT_PERMISSIONS),
      isActive: Boolean(row.isActive ?? row.is_active),
      status: row.status || "ACTIVE",
      mustChangePassword: Boolean(row.mustChangePassword ?? row.must_change_password),
    });
    setFormOpen(true);
  }

  async function submit() {
    if (!form.restaurantId) return setError("Restaurant is required");
    if (!form.name || !form.email) return setError("Name and email are required");
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        permissions: normalisePerms(form.permissions),
        isActive: form.isActive,
        mustChangePassword: form.mustChangePassword,
      };
      if (editing) {
        await restaurantAdminService.updateRestaurantEmployee(employeeIdOf(editing), payload);
      } else {
        const res = await restaurantAdminService.createRestaurantEmployee(form.restaurantId, payload);
        const data = unwrap(res, {});
        const password = data?.tempPassword || data?.data?.tempPassword || data?.employee?.tempPassword || payload.password || "";
        setCreatedPassword(password);
      }
      if (!editing) setForm({ ...blankForm, restaurantId: form.restaurantId });
      setEditing(null);
      setFormOpen(false);
      await loadEmployees();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  async function applyEmployeeAccess(row, nextStatus) {
    const employeeId = employeeIdOf(row);
    const statusValue = String(nextStatus || "").toUpperCase();
    if (!employeeId) return setError("Employee id missing");
    if (!["ACTIVE", "INACTIVE", "BLOCKED"].includes(statusValue)) return setError("Invalid employee status");

    setAccessSavingId(employeeId);
    setError("");
    try {
      if (statusValue === "ACTIVE") await restaurantAdminService.activateRestaurantEmployee(employeeId);
      else if (statusValue === "INACTIVE") await restaurantAdminService.deactivateRestaurantEmployee(employeeId);
      else await restaurantAdminService.blockRestaurantEmployee(employeeId);
      await loadEmployees();
    } catch (err) {
      setError(err);
    } finally {
      setAccessSavingId("");
      setConfirmDialog(null);
    }
  }

  function setEmployeeAccess(row, nextStatus) {
    const statusValue = String(nextStatus || "").toUpperCase();
    const employeeName = row?.name || "this employee";

    if (statusValue === "INACTIVE" || statusValue === "BLOCKED") {
      setConfirmDialog({
        tone: statusValue === "BLOCKED" ? "danger" : "warning",
        title: statusValue === "BLOCKED" ? "Block employee?" : "Deactivate employee?",
        message:
          statusValue === "BLOCKED"
            ? `${employeeName} will not be able to login and active sessions may be revoked.`
            : `${employeeName} will be marked inactive and cannot use the restaurant dashboard until reactivated.`,
        confirmText: statusValue === "BLOCKED" ? "Block Employee" : "Deactivate",
        onConfirm: () => applyEmployeeAccess(row, statusValue),
      });
      return;
    }

    return applyEmployeeAccess(row, statusValue);
  }

  async function submitReview(nextStatus) {
    if (!reviewing) return;
    if (nextStatus === "REJECTED" && !String(reviewNote || "").trim()) {
      setError("Reject note is required");
      return;
    }
    setReviewSaving(true);
    setError("");
    try {
      await restaurantAdminService.reviewRestaurantEmployeeOnboarding(employeeIdOf(reviewing), {
        status: nextStatus,
        note: reviewNote || (nextStatus === "APPROVED" ? "Documents verified successfully" : "Rejected by admin"),
      });
      setReviewing(null);
      setReviewNote("");
      await loadEmployees();
    } catch (err) {
      setError(err);
    } finally {
      setReviewSaving(false);
    }
  }

  async function approveEmployeeFromTable(row) {
    const employeeId = employeeIdOf(row);
    if (!employeeId) return setError("Employee id missing");
    setReviewSaving(true);
    setError("");
    try {
      await restaurantAdminService.reviewRestaurantEmployeeOnboarding(employeeId, {
        status: "APPROVED",
        note: "Documents verified successfully",
      });
      await loadEmployees();
    } catch (err) {
      setError(err);
    } finally {
      setReviewSaving(false);
    }
  }

  async function copyPassword() {
    if (!createdPassword) return;
    try {
      await navigator.clipboard.writeText(createdPassword);
    } catch (_) {}
  }

  if (loading) return <LoadingBox label="Loading employees..." />;

  return (
    <div>
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
         
          <h1 className="text-[22px] font-bold uppercase tracking-wide text-[#d96a7a] sm:text-[24px] lg:text-[28px]">Employees</h1>
          <p className="mt-1 max-w-[720px] text-[12px] font-medium leading-5 text-slate-500 sm:text-[13px]">
            Create, view, approve, activate, deactivate and block restaurant dashboard employees.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
          <Button variant="ghost" onClick={load}><RefreshCw size={16} /> Refresh</Button>
          <Button onClick={startCreate}><UserPlus size={16} /> Add New Employee</Button>
        </div>
      </div>

      <ErrorBox error={error} onRetry={load} />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryMetricCard label="Total Employees" value={employeeSummary.total} tone="pink" />
        <SummaryMetricCard label="Pending Approvals" value={employeeSummary.pending} tone="yellow" />
        <SummaryMetricCard label="Approved" value={employeeSummary.approved} tone="green" />
        <SummaryMetricCard label="Rejected" value={employeeSummary.rejected} tone="red" />
      </div>

      <div className="relative z-30 mb-5 flex flex-col gap-3 rounded-[20px] border border-[#f2c6cc] bg-white p-3 sm:p-4 xl:flex-row xl:items-end xl:justify-between">
       <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_minmax(190px,.9fr)_minmax(150px,.75fr)_minmax(170px,.85fr)] xl:items-end">
  <Input label="Search employee" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email or phone" />
  <ThemedDropdown label="Restaurant" value={restaurantId} onChange={setRestaurantId} options={restaurantFilterOptions} />
  <ThemedDropdown label="Status" value={status} onChange={setStatus} options={statusFilterOptions} />
  <ThemedDropdown label="Approval" value={approvalStatus} onChange={setApprovalStatus} options={approvalFilterOptions} />
</div>
        <div className="inline-flex h-10 w-full shrink-0 items-center justify-center rounded-[12px] border border-[#f2c6cc] bg-white px-4 text-[13px] font-bold text-[#d86a78] sm:w-auto xl:self-end">
          {employees.length} Visible Employees
        </div>
      </div>

      {createdPassword ? (
        <div className="mb-5 rounded-[18px] border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 font-extrabold"><CheckCircle2 size={18} /> Employee created</div>
              <div className="mt-1 text-[13px]">Temporary password: <span className="rounded-lg bg-white px-2 py-1 font-mono font-bold">{createdPassword}</span></div>
              <div className="mt-1 text-[12px]">Password email is sent by auth-service. Keep this one-time backup only for testing.</div>
            </div>
            <Button variant="ghost" onClick={copyPassword}><Copy size={15} /> Copy Password</Button>
          </div>
        </div>
      ) : null}

      <Card>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-[20px] font-bold text-[#d96a7a]">Employees</h2>
            <p className="text-[12px] text-slate-500">
              Showing {visibleFrom}-{visibleTo} of {employees.length} employees
            </p>
          </div>
          <div className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-[#fff0f2] text-[#d86a78]"><Users size={20} /></div>
        </div>
        {employees.length ? (
          <div className="-mx-3 overflow-x-auto overflow-y-visible px-3 pb-2 sm:mx-0 sm:px-0 [scrollbar-width:thin]">
            <table className="w-full min-w-[1120px] text-[12px] md:text-[13px]">
              <thead>
                <tr className="border-b text-[12px] text-bold uppercase tracking-wide text-[#d96a7a]">
                  <th className="px-3 py-3 md:px-4 text-center">Status</th>
                  <th className="px-3 py-3 md:px-4 text-left">Employee</th>
                  <th className="px-3 py-3 md:px-4 text-left">Contact</th>
                  <th className="px-3 py-3 md:px-4 text-left">Restaurant</th>
                  <th className="px-3 py-3 md:px-4 text-center">Role</th>
                  <th className="px-3 py-3 md:px-4 text-center">Onboarding</th>
                  <th className="px-3 py-3 md:px-4 text-center">Approval</th>
                  <th className="px-3 py-3 md:px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEmployees.map((e) => {
                  const rid = e.restaurantId || e.restaurant_id;
                  const appr = approvalStatusOf(e);
                  return (
                    <tr key={employeeIdOf(e)} className="border-b align-middle last:border-0">
                      <td className="px-3 py-4 md:px-4 text-center align-middle"><StatusPill value={e.status || (e.isActive || e.is_active ? "ACTIVE" : "INACTIVE")} /></td>
                      <td className="px-3 py-4 md:px-4 text-left align-middle">
                        <div className="max-w-[220px]">
                          <div className=" text-slate-1500">{e.name || "-"}</div>
                        </div>
                      </td>
                      <td className="px-3 py-4 md:px-4 text-left align-middle">
                        <div className="max-w-[270px] space-y-1 text-[12px] font-semibold text-slate-500">
                          <div className="flex items-center gap-2 break-all">
                            <Mail size={13} className="shrink-0 text-[#d86a78]" />
                            <span>{e.email || "-"}</span>
                          </div>
                          <div className="flex items-center gap-2 break-all">
                            <Phone size={13} className="shrink-0 text-[#d86a78]" />
                            <span>{e.phone || "No phone"}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4 md:px-4 text-left align-middle">
                        <div className="max-w-[220px]  text-slate-700">{getRestaurantName(restaurants, rid)}</div>
                      </td>
                      <td className="px-3 py-4 md:px-4 text-center align-middle">{e.role || "restaurant_manager"}</td>
                      <td className="px-3 py-4 md:px-4 text-center align-middle"><StatusPill value={onboardingCompletedOf(e) ? "COMPLETE" : "PENDING"} /></td>
                      <td className="px-3 py-4 md:px-4 text-center align-middle"><StatusPill value={appr} /></td>
                      
                      <td className="px-3 py-4 md:px-4 text-center align-middle">
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <button
                            type="button"
                            disabled={reviewSaving || appr !== "PENDING"}
                            onClick={() => { setReviewing(e); setReviewNote(""); }}
                            className="inline-flex h-9 items-center justify-center rounded-[12px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-bold text-[#d86a78] transition hover:bg-[#fff8f9] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Reject
                          </button>
                          <button
                            type="button"
                            disabled={reviewSaving || appr !== "PENDING"}
                            onClick={() => approveEmployeeFromTable(e)}
                            className="inline-flex h-9 items-center justify-center rounded-[12px] bg-[#d86a78] px-4 text-[12px] font-bold text-white transition hover:bg-[#c95b69] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <IconAction title="Edit employee" tone="review" disabled={accessSavingId === employeeIdOf(e)} onClick={() => startEdit(e)}>
                            <Pencil size={18} />
                          </IconAction>
                          <IconAction title="View employee profile" tone="review" disabled={accessSavingId === employeeIdOf(e)} onClick={() => { setReviewing(e); setReviewNote(""); }}>
                            <UserCheck size={18} />
                          </IconAction>
                          <IconAction
                            title={String(e.status || "").toUpperCase() === "BLOCKED" ? "Activate employee" : Boolean(e.isActive ?? e.is_active) ? "Deactivate employee" : "Activate employee"}
                            tone="success"
                            disabled={accessSavingId === employeeIdOf(e)}
                            onClick={() => setEmployeeAccess(e, String(e.status || "").toUpperCase() === "BLOCKED" ? "ACTIVE" : Boolean(e.isActive ?? e.is_active) ? "INACTIVE" : "ACTIVE")}
                          >
                            <Power size={18} />
                          </IconAction>
                          {String(e.status || "").toUpperCase() !== "BLOCKED" ? (
                            <IconAction title="Block employee" tone="danger" disabled={accessSavingId === employeeIdOf(e)} onClick={() => setEmployeeAccess(e, "BLOCKED")}>
                              <Ban size={18} />
                            </IconAction>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <EmptyState title="No restaurant employees" subtitle="Create the first restaurant employee from New Employee button." />}

        {employees.length ? (
          <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-[12px] font-medium text-slate-500">
              Showing {visibleFrom}-{visibleTo} of {employees.length}
            </div>

            <div className="flex flex-wrap items-center justify-start gap-2 sm:justify-end">
              <label className="flex items-center gap-2 text-[12px] font-medium text-slate-500">
                Rows
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-9 rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] font-semibold text-slate-700 outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
                >
                  {rowSizeOptions.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </label>

              <button
                type="button"
                disabled={safePage === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="inline-flex h-9 items-center justify-center rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#fff8f9] disabled:cursor-not-allowed disabled:opacity-45"
              >
                Previous
              </button>

              <div className="inline-flex h-9 items-center justify-center rounded-[10px] border border-[#f2c6cc] bg-[#fff8f9] px-4 text-[12px] font-semibold text-[#d86a78]">
                Page {safePage} / {totalPages}
              </div>

              <button
                type="button"
                disabled={safePage === totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                className="inline-flex h-9 items-center justify-center rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#fff8f9] disabled:cursor-not-allowed disabled:opacity-45"
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </Card>

      {formOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-2 sm:p-4">
          <div className="max-h-[94vh] w-full max-w-[1000px] overflow-auto rounded-[26px] bg-gradient-to-br from-white via-[#fff8f9] to-[#fff0f2] p-4 shadow-2xl sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-white text-[#d86a78] shadow-sm"><ShieldCheck size={21} /></div>
                <div>
                  <h2 className="text-[19px] font-semibold text-[#d86a78]">{editing ? "Edit employee" : "Create employee"}</h2>
                  <p className="text-[12px] text-slate-500">{editing ? "Update basic profile and permissions" : "Temp password will be emailed"}</p>
                </div>
              </div>
              <button onClick={closeForm} className="rounded-full border border-[#f2c6cc] bg-white p-2 text-[#d86a78]"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Select label="Restaurant" value={form.restaurantId} onChange={(e) => setField("restaurantId", e.target.value)} disabled={Boolean(editing)}>
                <option value="">Select restaurant</option>
                {restaurants.map((r) => <option key={restaurantIdOf(r)} value={restaurantIdOf(r)}>{r.name}</option>)}
              </Select>
              {form.restaurantId ? <div className="flex items-center rounded-[12px] bg-white px-3 py-2 text-[12px] font-bold text-[#d86a78]">Selected: {selectedRestaurantName}</div> : null}
              <Input label="Name" value={form.name} onChange={(e) => setField("name", e.target.value)} />
              <Input label="Email" value={form.email} onChange={(e) => setField("email", e.target.value)} disabled={Boolean(editing)} />
              <Input label="Phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
              {!editing ? <Input label="Temp password optional" value={form.password} onChange={(e) => setField("password", e.target.value)} placeholder="Blank = auto generate" /> : null}
              <Select label="Role" value={form.role} onChange={(e) => setField("role", e.target.value)}>
                <option value="restaurant_manager">restaurant_manager</option>
                <option value="restaurant_staff">restaurant_staff</option>
              </Select>
              <div className="md:col-span-2">
                <PermissionCheckboxes value={form.permissions} onChange={(next) => setField("permissions", next)} />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:col-span-2">
                <label className="flex items-center gap-3 rounded-[12px] bg-white p-3 text-[13px] font-bold text-slate-700"><input type="checkbox" checked={form.isActive} onChange={(e) => setField("isActive", e.target.checked)} /> Active</label>
                <label className="flex items-center gap-3 rounded-[12px] bg-white p-3 text-[13px] font-bold text-slate-700"><input type="checkbox" checked={form.mustChangePassword} onChange={(e) => setField("mustChangePassword", e.target.checked)} /> Change pass</label>
              </div>
              <Select label="Status" value={form.status} onChange={(e) => setField("status", e.target.value)}>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
                <option value="BLOCKED">BLOCKED</option>
              </Select>
              <div className="flex justify-end gap-2 pt-2 md:col-span-2">
                <Button variant="ghost" onClick={closeForm}>Cancel</Button>
                <Button disabled={saving} onClick={submit}>{saving ? "Saving..." : editing ? "Update Employee" : "Create Employee"}</Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDialog ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/35 p-3 sm:p-4">
          <div className="w-full max-w-[430px] rounded-[24px] border border-[#f2c6cc] bg-white p-4 shadow-2xl sm:rounded-[26px] sm:p-5">
            <div className="flex items-start gap-3">
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${confirmDialog.tone === "danger" ? "bg-rose-50 text-rose-500" : "bg-[#fff0f2] text-[#d86a78]"}`}>
                <AlertTriangle size={23} />
              </div>
              <div>
                <h3 className="text-[19px] font-semibold text-[#d86a78]">{confirmDialog.title}</h3>
                <p className="mt-1 text-[13px] leading-5 text-[#d86a78]">{confirmDialog.message}</p>
              </div>
            </div>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={Boolean(accessSavingId)}
                onClick={() => setConfirmDialog(null)}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-[#f2c6cc] bg-white px-4 text-[13px] font-semibold text-[#d86a78] transition hover:bg-[#fff8f9] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={Boolean(accessSavingId)}
                onClick={confirmDialog.onConfirm}
                className={`inline-flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${confirmDialog.tone === "danger" ? "bg-rose-500 hover:bg-rose-600" : "bg-[#d86a78] hover:bg-[#c95b69]"}`}
              >
                {accessSavingId ? "Please wait..." : confirmDialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {reviewing ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-2 sm:p-4">
          <div className="max-h-[94vh] w-full max-w-[1000px] overflow-auto rounded-[26px] bg-gradient-to-br from-white via-[#fff8f9] to-[#fff0f2] p-4 shadow-2xl sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-white text-[#d86a78] shadow-sm"><UserCheck size={21} /></div>
                <div>
                  <h2 className="text-[19px] font-semibold text-[#d86a78]">Employee profile</h2>
                  <p className="text-[12px] text-slate-500">View complete employee onboarding details, bank/KYC data, and uploaded images.</p>
                </div>
              </div>
              <button onClick={() => setReviewing(null)} className="rounded-full border border-[#f2c6cc] bg-white p-2 text-[#d86a78]"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="rounded-[16px] border border-[#f2c6cc] bg-[#fff8f9] p-4">
                <div className="text-[12px] font-bold uppercase text-slate-400">Employee</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{reviewing.name}</div>
                <div className="text-[13px] text-slate-500">{reviewing.email} · {reviewing.phone || "No phone"}</div>
              </div>
              <div className="rounded-[16px] border border-[#f2c6cc] bg-[#fff8f9] p-4">
                <div className="text-[12px] font-bold uppercase text-slate-400">Restaurant</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{getRestaurantName(restaurants, reviewing.restaurantId || reviewing.restaurant_id)}</div>
                <div className="text-[13px] text-slate-500">Approval: <StatusPill value={approvalStatusOf(reviewing)} /></div>
              </div>
            </div>
            <div className="mt-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[17px] font-semibold text-[#d86a78]">Employee Profile & Onboarding</div>
                  <div className="text-[12px] text-slate-500">Personal details, complete address, KYC/bank info and uploaded images.</div>
                </div>
                <StatusPill value={onboardingCompletedOf(reviewing) ? "COMPLETE" : "PENDING"} />
              </div>
              <OnboardingDetails value={onboardingPayloadOf(reviewing)} employee={reviewing} />
            </div>
            <div className="mt-4">
              <Textarea label="Admin note" value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Required for rejection. Optional for approval." />
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => setReviewing(null)}>Close</Button>
              <Button variant="ghost" disabled={reviewSaving} onClick={() => submitReview("REJECTED")}>Reject</Button>
              <Button disabled={reviewSaving} onClick={() => submitReview("APPROVED")}>{reviewSaving ? "Saving..." : "Approve"}</Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
